import React, { useState } from "react";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { useStateSelector } from "utils/field";
import { getValidation } from "utils/fieldValidation";
import { getDhwTemplate, getEStarManufacturerOptions, getEStarModelOptions, getEStarOptions } from "utils/mechanicals";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Button from "components/Button";
import Accordion from "components/Accordion";
import DhwSystem from "./DhwSystem";
import AddBox from "components/AddBox";
import Drawer from "components/Drawer";
import AddIcon from "assets/images/icons/JSX/Add";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

// White AddIcon for use on dark blue buttons
const WhiteAddIcon = ({ className = "" }) => (
    <svg
        className={className}
        width="16px"
        height="16px"
        viewBox="0 0 16 16"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        style={{ fill: "white" }}
    >
        <g
            id="Accordion-/-Hover-Elements-/-Dupe-+-Trash-+-Drag"
            transform="translate(0.000000, -5.000000)"
            fill="white"
            fillRule="nonzero"
        >
            <g id="Group-2" transform="translate(-4.000000, 0.000000)">
                <g
                    id="Group"
                    transform="translate(12.000000, 13.000000) rotate(-45.000000) translate(-12.000000, -13.000000) translate(4.000000, 5.000000)"
                >
                    <path
                        d="M8,0 C12.418278,0 16,3.581722 16,8 C16,12.418278 12.418278,16 8,16 C3.581722,16 0,12.418278 0,8 C0,3.581722 3.581722,0 8,0 Z M8,1 C4.13400675,1 1,4.13400675 1,8 C1,11.8659932 4.13400675,15 8,15 C11.8659932,15 15,11.8659932 15,8 C15,4.13400675 11.8659932,1 8,1 Z M10.1213203,5.17157288 L10.8284271,5.87867966 L8.707,8 L10.8284271,10.1213203 L10.1213203,10.8284271 L8,8.707 L5.87867966,10.8284271 L5.17157288,10.1213203 L7.293,8 L5.17157288,5.87867966 L5.87867966,5.17157288 L8,7.293 L10.1213203,5.17157288 Z"
                        id="Oval-Copy"
                    ></path>
                </g>
            </g>
        </g>
    </svg>
);

const dwhrMURBNumLowEffValidation = getValidation("dwhrMURBNumLowEff");
const dwhrMURBNumHighEffValidation = getValidation("dwhrMURBNumHighEff");
const dhwMURBEStarInstCondensingValidation = getValidation("dhwMURBEStarInstCondensing");
const dhwMURBEStarInstValidation = getValidation("dhwMURBEStarInst");
const dhwMURBCondensingValidation = getValidation("dhwMURBCondensing");
const dhwMURBInstValidation = getValidation("dhwMURBInst");
const dhwMURBHeatPumpValidation = getValidation("dhwMURBHeatPump");
const eStarManufacturerValidation = getValidation("dhwEStarManufacturer");
const eStarModelValidation = getValidation("dhwEStarModel");

export default ({
    accessor,
    favId,
    change,
    formName,
    isUpgrade = false,
    contentOpen,
    canBeEditedFromParametric = true,
    isParametricAnalysis = false,
    upgradeType,
    associatedHeatingSystem,
}) => {
    const dispatch = useDispatch();

    const [open, toggleOpen] = useState(false);
    const [eStarManufacturerValue, setEStarManufacturerValue] = useState("");
    const [eStarModelId, setEStarModelId] = useState("");

    const isPrimaryPresent = Object.keys(useStateSelector(`${accessor}.primarySystem`) || {}).length > 3;
    const isSecondaryPresent = Object.keys(useStateSelector(`${accessor}.secondarySystem`) || {}).length > 3;

    const { id: buildingTypeId } =
        useStateSelector(
            isParametricAnalysis
                ? `parametricAnalysis.parametricAnalysis.model.modelData.specifications.buildingType`
                : `model.modelData.specifications.buildingType`
        ) || {};
    const isMURB = buildingTypeId === 2; //Whole building MURB

    const isMURBEmpty = isEmpty(useStateSelector(`${accessor}.murb`)) || {};

    const activeSystem = useStateSelector(`${accessor}.activeSystem`) || "primary";

    const modelUnits =
        useStateSelector(
            isParametricAnalysis
                ? `parametricAnalysis.parametricAnalysis.model.modelData.uiSettings.primaryUnits`
                : `model.modelData.uiSettings.primaryUnits`
        ) || {};

    let heatingSysType = Object.keys(
        useStateSelector(
            isParametricAnalysis
                ? `parametricAnalysis.parametricAnalysis.model.modelData.heatingCooling.heating.system`
                : `model.modelData.heatingCooling.heating.system`
        ) || {}
    )[0];

    if (formName === "upgradeDrawer") {
        heatingSysType = associatedHeatingSystem || heatingSysType;
    }

    const basementComponents = useStateSelector(`model.modelData.components.basement`) || {};

    let noBasementComponents = false;
    if (isEmpty(basementComponents)) {
        noBasementComponents = true;
    } else {
        noBasementComponents = Object.keys(basementComponents)
            .reduce((acc, key) => [...acc, isEmpty(basementComponents[key])], [])
            .every((el) => el);
    }

    const { minor: h2kMinor } = useStateSelector("model.modelData.h2kVersion") || {};

    const isPrimaryDhwParametric = (isParametricAnalysis && upgradeType === "primaryHotWater") || false;
    const isDwhrParametric = (isParametricAnalysis && upgradeType === "dwhr") || false;

    const createHeaderLabel = () => {
        if (activeSystem === "secondary") {
            return "Secondary";
        }
        return "Primary";
    };

    const handleDrawerOpen = (sysType) => {
        change(`${accessor}.activeSystem`, sysType);
        toggleOpen(true);
    };

    const handleQuickCreate = (sysType) => {
        handleCreateDhw({
            sysType,
            manufacturer: false,
            modelId: false,
            favId: false,
            noBasementComponents: noBasementComponents,
        });
    };

    const handleCreateDhw = ({ sysType, manufacturer, modelId, favId, noBasementComponents }) => {
        if (favId) {
            //Favourite System
            return;
        } else if (manufacturer && modelId) {
            //EStar
            const { primarySystem: eStarTemplate = {} } = getEStarOptions("dhw")[modelId] || {};
            const { energyFactor: { id, value, inputCapacity = 0 } = {} } = eStarTemplate;
            change(`${accessor}.${sysType}System`, {
                ...eStarTemplate,
                tankLocation: {
                    id: noBasementComponents ? 0 : 1,
                },
                energyFactor: { id, value },
            });
            if (inputCapacity > 0) {
                change(`${accessor}.${sysType}System.standby.inputCapacity`, inputCapacity);
            }

            return;
        } else if ((sysType === "primary" && !isPrimaryPresent) || (sysType === "secondary" && !isSecondaryPresent)) {
            change(`${accessor}.${sysType}System`, {
                ...getDhwTemplate("dhw"),
                tankLocation: {
                    id: noBasementComponents ? 0 : 1,
                },
            });

            if (sysType === "secondary") {
                change(`${accessor}.primarySystem.fractionOfTank`, 1);
            }

            return;
        }
    };

    const handleDeleteDhw = (sysType) => {
        if ((sysType === "primary" && isPrimaryPresent) || (sysType === "secondary" && isSecondaryPresent)) {
            change(`${accessor}.${sysType}System`, {});

            dispatch(removeFormErrors(`${accessor}.${sysType}System`));

            if (sysType === "secondary") {
                change(`${accessor}.primarySystem.fractionOfTank`, "NaN");
                dispatch(removeFormErrors(`${accessor}.primarySystem.fractionOfTank`));
            }
        }
    };

    const isCombo = ["combo", "p9Combo"].includes(heatingSysType);

    return (
        <>
            <Drawer
                open={open}
                close={() => toggleOpen(false)}
                preTitle="New System"
                title={`Import ENERGY STAR ${createHeaderLabel()} Domestic Hot Water System`}
                topBarColor="#0eb0a9"
            >
                <div>
                    <div style={{ marginBottom: "2rem" }}>
                        <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#666" }}>
                            Import specifications from an ENERGY STAR certified system. This will replace the default
                            specifications.
                        </p>
                        <InputRow>
                            <Select
                                name="eStarManufacturer"
                                options={[
                                    {
                                        value: false,
                                        label: "None",
                                    },
                                    ...getEStarManufacturerOptions("dhw"),
                                ]}
                                label="ENERGY STAR Manufacturer"
                                placeholder="Choose Manufacturer"
                                validate={eStarManufacturerValidation}
                                disabled={!canBeEditedFromParametric}
                                info={"Select a manufacturer of an ENERGY STAR domestic hot water system"}
                                search={true}
                                input={{
                                    value: eStarManufacturerValue,
                                    onChange: (value) => setEStarManufacturerValue(value),
                                }}
                            />
                        </InputRow>
                        <InputRow>
                            <Select
                                name="eStarModelId"
                                options={[
                                    {
                                        value: false,
                                        label: "None",
                                    },
                                    ...getEStarModelOptions({
                                        systemType: "dhw",
                                        manufacturer: eStarManufacturerValue,
                                    }),
                                ]}
                                label="ENERGY STAR Model"
                                placeholder="Choose Model"
                                validate={eStarModelValidation}
                                disabled={!eStarManufacturerValue || !canBeEditedFromParametric}
                                info={"Select an ENERGY STAR certified model"}
                                search={true}
                                input={{
                                    value: eStarModelId,
                                    onChange: (value) => setEStarModelId(value),
                                }}
                            />
                        </InputRow>
                        <div
                            className={classes.buttons}
                            style={{
                                marginTop: "1.5rem",
                                display: "flex",
                                flexDirection: "column",
                                gap: "1.25rem",
                            }}
                        >
                            <Button
                                onClick={() => {
                                    handleCreateDhw({
                                        sysType: activeSystem,
                                        manufacturer: eStarManufacturerValue,
                                        modelId: eStarModelId,
                                        noBasementComponents: noBasementComponents,
                                    });
                                    toggleOpen(false);
                                }}
                                disabled={!eStarManufacturerValue || !eStarModelId || !canBeEditedFromParametric}
                            >
                                Import ENERGY STAR System
                            </Button>
                        </div>
                    </div>

                    <div
                        className={classes.buttons}
                        style={{ marginTop: "2rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}
                    >
                        <Button large type="hollow" onClick={() => toggleOpen(false)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Drawer>
            {isCombo && !isUpgrade && (
                <div className={sharedClasses.section}>
                    <div className={classes.heading}>
                        <div className={classes.title}>
                            <h4>
                                Hot water is controlled by a combo heating/DHW system, and can be edited in the Heating
                                System tab
                            </h4>
                        </div>
                    </div>
                </div>
            )}
            {!isCombo && (
                <>
                    {isPrimaryPresent ? (
                        <DhwSystem
                            accessor={`${accessor}.primarySystem`}
                            change={change}
                            isPrimary={true}
                            handleDelete={() => handleDeleteDhw("primary")}
                            hasSecondarySystem={isSecondaryPresent}
                            isCombo={isCombo}
                            noBasementComponents={noBasementComponents}
                            formName={formName}
                            isUpgrade={isUpgrade}
                            isDwhrParametric={isDwhrParametric}
                            isPrimaryDhwParametric={isPrimaryDhwParametric}
                        />
                    ) : (
                        <div className={!isUpgrade && classes.addBoxWrapper}>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "1rem",
                                    alignItems: "center",
                                    paddingTop: "1.5rem",
                                }}
                            >
                                <Button
                                    onClick={() => handleQuickCreate("primary")}
                                    disabled={!canBeEditedFromParametric}
                                    icon={WhiteAddIcon}
                                    style={{ minWidth: "300px" }}
                                >
                                    Create Default Primary System
                                </Button>
                                <Button
                                    type="hollow"
                                    onClick={() => handleDrawerOpen("primary")}
                                    disabled={!canBeEditedFromParametric}
                                    style={{ width: "280px" }}
                                >
                                    Import ENERGY STAR System
                                </Button>
                            </div>
                        </div>
                    )}
                    {isSecondaryPresent ? (
                        <DhwSystem
                            accessor={`${accessor}.secondarySystem`}
                            change={change}
                            isPrimary={false}
                            handleDelete={() => handleDeleteDhw("secondary")}
                            noBasementComponents={noBasementComponents}
                            formName={formName}
                            isUpgrade={isUpgrade}
                            isSecondary={isSecondaryPresent}
                        />
                    ) : !isDwhrParametric && !isPrimaryDhwParametric ? (
                        <>
                            <div
                                style={{
                                    height: "1px",
                                    backgroundColor: "#E5E7EB",
                                    margin: "1.5rem 0",
                                    marginLeft: isUpgrade ? 0 : "1.875rem",
                                    marginRight: isUpgrade ? 0 : "1.875rem",
                                }}
                            />
                            <div className={classes.addBoxWrapper} style={{ padding: isUpgrade ? 0 : "0 1.875rem" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "1rem",
                                        alignItems: "center",
                                    }}
                                >
                                    <Button
                                        onClick={() => handleQuickCreate("secondary")}
                                        disabled={!canBeEditedFromParametric}
                                        icon={WhiteAddIcon}
                                        style={{ minWidth: "300px" }}
                                    >
                                        Create Default Secondary System
                                    </Button>
                                    <Button
                                        type="hollow"
                                        onClick={() => handleDrawerOpen("secondary")}
                                        disabled={!canBeEditedFromParametric}
                                        style={{ width: "280px" }}
                                    >
                                        Import ENERGY STAR System
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                </>
            )}
            {isMURB && (
                <div
                    // className={sharedClasses.section}
                    className={classNames(sharedClasses.section, {
                        [classes.upgradeMurbSpecsContainer]: isUpgrade,
                    })}
                >
                    <Accordion
                        className={sharedClasses.accordion}
                        heading={<span className={sharedClasses.heading}>MURB Specifications</span>}
                        large
                        disabled={!isMURB}
                        forceOpen={contentOpen && isMURB}
                    >
                        <h4>Number of Hot Water Systems in Building</h4>
                        <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                            <Field
                                component={Input}
                                name={`${accessor}.murb.murbNumHotWaterSystems.eStarInstantaneousCondensing`}
                                type="number"
                                label="ENERGY STAR Instantaneous (Cond.)"
                                placeholder={0.0}
                                validate={dhwMURBEStarInstCondensingValidation}
                                disabled={!canBeEditedFromParametric}
                            />
                            <Field
                                component={Input}
                                name={`${accessor}.murb.murbNumHotWaterSystems.eStarInstantaneous`}
                                type="number"
                                label="ENERGY STAR Instantaneous"
                                placeholder={0.0}
                                validate={dhwMURBEStarInstValidation}
                                disabled={!canBeEditedFromParametric}
                            />
                            <Field
                                component={Input}
                                name={`${accessor}.murb.murbNumHotWaterSystems.condensing`}
                                type="number"
                                label="Condensing"
                                placeholder={0.0}
                                validate={dhwMURBCondensingValidation}
                                disabled={!canBeEditedFromParametric}
                            />
                            <Field
                                component={Input}
                                name={`${accessor}.murb.murbNumHotWaterSystems.instantaneous`}
                                type="number"
                                label="Instantaneous"
                                placeholder={0.0}
                                validate={dhwMURBInstValidation}
                                disabled={!canBeEditedFromParametric}
                            />
                        </InputRow>
                        {h2kMinor >= 11 && (
                            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                                <Field
                                    component={Input}
                                    name={`${accessor}.murb.murbNumHotWaterSystems.heatPumpWaterHeater`}
                                    type="number"
                                    label="Heat Pump Water Heaters"
                                    placeholder={0.0}
                                    validate={dhwMURBInstValidation}
                                    disabled={!canBeEditedFromParametric}
                                />
                            </InputRow>
                        )}
                        <h4>Number of Drain Water Heat Recovery Systems in Building</h4>
                        <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                            <Field
                                component={Input}
                                name={`${accessor}.murb.murbNumDwhrSystems.lowEfficiency`}
                                type="number"
                                label="Low Efficiency (<42%)"
                                placeholder={0.0}
                                validate={dwhrMURBNumLowEffValidation}
                                disabled={!canBeEditedFromParametric}
                            />
                            <Field
                                component={Input}
                                name={`${accessor}.murb.murbNumDwhrSystems.highEfficiency`}
                                type="number"
                                label="High Efficiency (>42%)"
                                placeholder={0.0}
                                validate={dwhrMURBNumHighEffValidation}
                                disabled={!canBeEditedFromParametric}
                            />
                        </InputRow>
                    </Accordion>
                </div>
            )}
        </>
    );
};
